// Delegate — the headless hook moved to `@heidi/workspace/data` (D2,
// ARCFE-1062). This file owns the app reactions (ARCFE-921): both toasts and the
// `showToast` option that selects between firing and not, the Rematch
// `analyticsScribe.saveSettings` dispatch, and the five app invalidation
// commands the package cannot reach —
// `cancelCurrentUserQueries` / `invalidateCurrentUser` (app helpers wrapping
// their queryClient call in a try/catch-and-log; the second is additionally
// COMPOSITE, invalidating GET_USER *and* the commerce GET_PRODUCTS),
// `invalidateNotificationPreferences` (workspace data, sequenced here),
// `invalidateCurrentUserLanguage` (`@/utils/translations`) and
// `invalidateSpecialties` (LOC-990, see the callback below). Sequenced here per
// delivery section 3; deliberately not injected through `configureWorkspaceData`.
import { saveSettings } from '@/lib/analytics/scribe/general';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { invalidateCurrentUserLanguage, useTranslations } from '@/utils/translations';
import { WORKFLOW_KEYS } from '@heidi/agents/contracts';
import { toast } from '@heidi/design-system/toast';
import {
  invalidateNotificationPreferences,
  UPDATE_PROFILE,
  useIsUpdatingSettings,
  useMutateUpdateSettings as useMutateUpdateSettingsCore,
} from '@heidi/workspace/data';
import { invalidateSpecialties } from '../query/useQuerySpecialties';
import { cancelCurrentUserQueries, invalidateCurrentUser } from '../query/useQueryUser';

/**
 * Refetch every cached routine-preset catalog (all locale/connector variants,
 * by prefix); the catalog copy is server-localised from the account language
 * this PATCH just persisted.
 */
const invalidateRoutinePresetCatalog = async () => {
  try {
    await reactQueryClient.invalidateQueries({ queryKey: WORKFLOW_KEYS.templates });
  } catch {
    logger.error('[routines] failed to invalidate the preset catalog cache');
  }
};

export { UPDATE_PROFILE, useIsUpdatingSettings };

export function useMutateUpdateSettings({
  showToast = true,
}: { showToast?: boolean } = {}) {
  const { t } = useTranslations();

  return useMutateUpdateSettingsCore({
    onSettingsMutate: async () => {
      await cancelCurrentUserQueries();
    },
    onSettingsSaved: (variables) => {
      if (showToast) {
        toast({
          tone: 'success',
          title: t('common.settingsUpdated'),
        });
      }

      // tracking
      saveSettings(variables);
    },
    onSettingsError: () => {
      if (showToast) {
        toast({
          tone: 'destructive',
          title: 'Backend Error',
        });
      }
    },
    onSettingsSettled: async () => {
      await invalidateCurrentUser();
      await invalidateNotificationPreferences();
    },
    // Fires only when the PATCH succeeded: the locale-keyed self-refetches
    // raced it at the optimistic `changeLocale`, so the lists cached under the
    // new locale key are in the old language. Refetch now that the backend
    // agrees with the FE locale.
    onDisplayLanguageChanged: async () => {
      await invalidateCurrentUserLanguage();
      await invalidateSpecialties();
      await invalidateRoutinePresetCatalog();
    },
  });
}
